import { AutomateTask, success } from "@iflyrpa/share";
import { USER_MESSAGE } from "../../utils/errorMessages";
import { Http } from "../../utils/http";
import type { FetchArticlesParams } from ".";
import { type BaijiahaoArticle, errorResponse, type FetchArticlesResponse } from "./types";

interface ArticleInfo {
    abstract: string;
    antispam_code: number;
    app_id: string;
    article_id: string;
    article_set_nids: string;
    audit_msg: string;
    authentication_type: string;
    auto_generate_id: string;
    bad_status: string;
    bjh_ext_info: string;
    bjh_video_finger_printing: string;
    call_slot: number;
    commit_at: string;
    content?: string;
    cover_images: string;
    cover_layout: string;
    created_at: string;
    creative_method: string;
    deleted_at: string;
    displaytype_exinfo: string;
    domain: string;
    dst_status: string;
    ext_info: string;
    feed_cat: string;
    feed_id: string;
    feed_id_from: number;
    feed_push_amount: number;
    feed_rs: string;
    feed_source_id: string;
    feed_sub_cat: string;
    file_name: string;
    first_exclusive_publish: number;
    first_storage_time: number;
    has_edited_times: number;
    is_authenticated: number;
    is_feed_import: number;
    is_good: number;
    is_pay: number;
    is_published: number;
    is_reported: number;
    is_transfer?: number;
    is_tagged: number;
    is_wx_spread: number;
    issue_status: number;
    midway_issue_status: number;
    mount_goods: number;
    mthvideo: number;
    nid: string;
    open_comment: number;
    open_front_page: number;
    origin_app_id: string;
    origin_id: string;
    origin_url: string;
    original_status: number;
    pay_money: number;
    platform_source: number;
    provider: string;
    publish_at: string;
    publish_time: string;
    publish_type: string;
    quality: number;
    quality_audit_type: string;
    quality_not_pass_reason: string;
    quality_status: string;
    res_slot: string;
    secure_not_pass_reason: string;
    secure_status: string;
    shoubai_c_articleid: string;
    smp_url: string;
    source_name: string;
    source_reprinted_allow: number;
    source_reprinted_astrict: string;
    source_type: string;
    status: string;
    sub_status_id: number;
    subtitle: string;
    tag: string;
    tag_detail: string;
    title: string;
    top_end: number;
    top_st: number;
    tp_id: string;
    transform_id: string;
    trigger_odyssey: number;
    type: string;
    updated_at: string;
    url: string;
    vertical_cover: string;
    video_sub_type: number;
    video_tag: string;
    weixin_import_type: number;
    id: string;
    certify_url: string;
    timer_time: number;
    similar_article_id: string;
    article_push_audit_res: number;
    withdraw_status: {
        withdraw_msg: string;
        has_withdraw_times: {
            news: number;
        };
        can_withdraw: number;
    };
    modify_status: {
        edit_msg: string;
        can_edit: number;
    };
    delete_status: {
        delete_msg: string;
        can_delete: number;
    };
    recover_status: {
        recover_msg: string;
        can_recover: number;
    };
    can_edit_goods: number;
    recomm_status: {
        can_recomm: number;
    };
    dxxFinalAuditResult: string;
    read_amount: number;
    read_num: number;
    forward_num: number;
    praise_num: number;
    comment_num: number;
    rec_amount: number;
    comment_amount: number;
    share_amount: number;
    like_amount: number;
    collection_amount: number;
    profit: number;
    is_rec: number;
    solicit_info: {
        tag: string | null;
        point: string | null;
        inspire: string | null;
    };
    group_can_share: number;
    group_already_shared: number;
    show_selected_comment_option: number;
    show_ai_intro: number;
    flow_invest_status: number;
    flow_invest_msg: string;
    package_invest_status: number;
    can_only_modify_goods: number;
    audit_msg_color: string;
    audit_msg_icon: string;
    playlet_pay2free_switch: number;
    audit_type_v0: string;
    is_ridewind: string;
    is_show_adding: string;
    is_show_add_view: string;
    add_view_count: string;
    coupons_add_view_count: string;
    platform_add_view_count: string;
    rec_count: number;
    share_url: string;
    v_type: number;
    v_intro: string;
    duration: number | null;
    video_num: number;
}

type ArticleApiResponse = {
    errno: number;
    data: {
        list: ArticleInfo[];
        page: {
            currentPage: number;
            pageSize: number;
            totalCount: number;
            totalPage: number;
        };
    };
};

export async function handleBaijiahaoData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    try {
        const {
            cookies,
            token,
            pageNum = 1,
            pageSize = 10,
            showOriginalData = false,
            onlySuccess = false,
            containsArticle = false,
        } = params;

        if (!token) return { code: 414, message: "缺少token", data: {} };
        const http = new Http({
            headers: {
                cookie: cookies.map((it) => `${it.name}=${it.value}`).join(";"),
                token: token,
            },
        });

        async function fetchArticles(
            pageNum: number,
            pageSize: number,
            onlySuccess = false,
        ): Promise<ArticleApiResponse> {
            return await http.api<ArticleApiResponse>({
                method: "get",
                url: "https://baijiahao.baidu.com/pcui/article/lists",
                params: {
                    currentPage: pageNum,
                    pageSize: pageSize,
                    search: "",
                    type: "",
                    collection: onlySuccess ? "publish" : "",
                    clearBeforeFetch: false,
                    dynamic: 1,
                },
            });
        }

        const articleInfo = await fetchArticles(pageNum, pageSize, onlySuccess);
        if (articleInfo.errno !== 0) return errorResponse("百家号数据获取失败，请检查账号状态", 414);
        const filtered = articleInfo.data.list.filter((item) => item.status !== "draft");
        const totalPage = articleInfo.data.page?.totalPage || 1;

        let currentPage = pageNum + 1;
        const usedPages = new Set<number>();
        while (filtered.length < pageSize && currentPage <= totalPage) {
            if (usedPages.has(currentPage)) {
                currentPage++;
                continue;
            }

            const res = await fetchArticles(currentPage, pageSize, onlySuccess);
            usedPages.add(currentPage);

            const validItems = res.data.list.filter((item) => item.status !== "draft");
            filtered.push(...validItems);

            currentPage++;
        }

        // 截取前 pageSize 条返回
        const final = filtered.slice(0, pageSize);

        const getDetailContent = async (url: string) => {
            try {
                const detailInfo = await http.api<string>({ method: "get", url });
                const match = detailInfo.match(/window\.jsonData\s*=\s*(\{[\s\S]*?\});window\.firstScreenTime/);
                if (!match) {
                    const isCheckPass = detailInfo.indexOf("百度安全验证");
                    if (isCheckPass > -1) {
                        return "⚠️ 需要进行百度安全验证，请手动打开链接完成验证后再尝试获取文章内容";
                    }
                    return null;
                }
                const jsonStr = match[1].trim();
                type DataType = {
                    bsData: {
                        superlanding: {
                            itemData: {
                                sections: {
                                    content: string;
                                }[];
                            };
                        }[];
                    };
                };
                const data: DataType = JSON.parse(jsonStr);
                return data.bsData.superlanding[0].itemData.sections.map((item) => item.content).join("");
            } catch (err) {
                console.error("❌ JSON 解析失败:", err);
                return null;
            }
        };

        // 转换结构
        const articleCell: BaijiahaoArticle[] = await Promise.all(
            final.map(async (item) => ({
                title: (item?.is_transfer ?? false) ? item.content || "" : item.title,
                imageUrl: JSON.parse(item.cover_images)[0]?.src || "",
                createTime: Math.floor(new Date(item.publish_at.replace(/-/g, "/")).getTime() / 1000),
                redirectUrl: item.url || item.share_url,
                recommendNum: item.rec_amount | item.forward_num,
                collectNum: item.collection_amount,
                readNum: item.read_amount | item.read_num,
                likeNum: item.like_amount | item.praise_num,
                commentNum: item.comment_amount | item.comment_num,
                shareNum: item.share_amount,
                ...(showOriginalData ? { originalData: item } : {}),
                ...(containsArticle ? { article: await getDetailContent(item.url) } : {}),
            })),
        );

        return success(
            {
                articleCell,
                ...(onlySuccess
                    ? {
                          pagination: {
                              total: articleInfo.data.page?.totalCount,
                              pageNum,
                              pageSize,
                          },
                      }
                    : null),
            },
            "百家号文章数据获取成功",
        );
    } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        _task.logger.error(`百家号文章数据获取失败: ${errMsg}`);
        return errorResponse(USER_MESSAGE.BJH_POST_FETCH_FAILED);
    }
}
